import CustomTable from "@/components/CustomTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchIcon } from "@/assets";
import { Plus } from "lucide-react";
import { useState, useMemo, useCallback, useEffect } from "react";
import { getColumns, type MarketRateProposal } from "./columns";
import AddNewRate from "./common/AddNewRate";
import EditRate from "./common/EditRate";
import DeleteConfirm from "./common/DeleteConfirm";
import { getMarketSettings, getMarketPairs } from "@/services/Market.service";

const Markets = () => {
  const [search, setSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [addRateOpen, setAddRateOpen] = useState(false);
  const [marketRates, setMarketRates] = useState<{ data: any[], meta: any }>({ data: [], meta: {} });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pairs, setPairs] = useState<any[]>([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairsError, setPairsError] = useState<string | null>(null);


  // Edit state
  const [editRateOpen, setEditRateOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<MarketRateProposal | null>(null);

  // Delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRate, setDeletingRate] = useState<MarketRateProposal | null>(null);

  const fetchMarketRates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getMarketSettings({
        search,
        currency: currencyFilter === "all" ? "" : currencyFilter,
        type: typeFilter === "all" ? "" : typeFilter,
      });
      setMarketRates(response.data.data);
    } catch (err) {
      setError("Failed to fetch market rates");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMarketPairs = async () => {
    setPairsLoading(true);
    setPairsError(null);
    try {
      const response = await getMarketPairs();
      if (response && response.data && Array.isArray(response.data.data)) {
        setPairs(response.data.data);
      } else {
        setPairs([]);
      }
    } catch (err) {
      setPairsError("Failed to fetch market pairs");
      console.error(err);
      setPairs([]);
    } finally {
      setPairsLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketRates();
  }, [search, currencyFilter, typeFilter]);

  useEffect(() => {
    fetchMarketPairs();
  }, []); // Fetch pairs only once on component mount

  const handleEdit = useCallback((row: MarketRateProposal) => {
    setEditingRate(row);
    setEditRateOpen(true);
  }, []);

  const handleDelete = useCallback((row: MarketRateProposal) => {
    setDeletingRate(row);
    setDeleteOpen(true);
  }, []);

  const columns = useMemo(
    () => getColumns({ onEdit: handleEdit, onDelete: handleDelete }),
    [handleEdit, handleDelete]
  );

  const transformedData = useMemo(() => {
    return Array.isArray(marketRates.data) ? marketRates.data.map((rate: any) => {
      const sp = rate.supportedPair ?? {};
      const basePrice = sp.price ?? 0;

      let buy = basePrice;
      let sell = basePrice;

      // Assuming MarketSettingOffset corresponds to rate.offset
      switch (rate.offset) {
        case "MANUAL": // Corresponds to MarketSettingOffset.MANUAL
          buy = rate.buying;
          sell = rate.selling;
          break;
        case "FLAT": // Corresponds to MarketSettingOffset.FLAT
          buy = basePrice + rate.buying;
          sell = basePrice + rate.selling;
          break;
        case "PERCENTAGE": // Corresponds to MarketSettingOffset.PERCENTAGE
          buy = basePrice + (rate.buying / 100) * basePrice;
          sell = basePrice + (rate.selling / 100) * basePrice;
          break;
        default:
          buy = basePrice;
          sell = basePrice;
      }

      const buyType = rate.offset as MarketRateProposal["buyType"];

      const sellTypeCandidates = [
        rate.sellOffsetType,
        rate.sellingOffsetType,
        rate.sellType,
        typeof rate.sellingOffset === "string" ? rate.sellingOffset : null,
      ];
      const sellTypeRaw = sellTypeCandidates.find(
        (v) =>
          typeof v === "string" &&
          ["FLAT", "MANUAL", "PERCENTAGE"].includes(v)
      ) as MarketRateProposal["sellType"] | undefined;
      const sellType = sellTypeRaw ?? buyType;

      return {
        id: rate.id,
        currency: `${sp.base}/${sp.quote}`,
        buyingOffset: rate.buying,
        sellingOffset: rate.selling,
        buyType,
        sellType,
        type: buyType,
        buyingRate: buy,
        sellingRate: sell,
        buyRate: sp.buyRate ?? sp.price,
        sellRate: sp.sellRate ?? sp.price,
        buyRateProvider: sp.buyRateProvider ?? null,
        sellRateProvider: sp.sellRateProvider ?? null,
        supportedPairId: sp.id,
      };
    }) : [];
  }, [marketRates.data]);

  const filteredData = useMemo(() => {
    return transformedData.filter((row) => {
      const matchesSearch =
        !search ||
        row.currency.toLowerCase().includes(search.toLowerCase());
      const matchesCurrency =
        currencyFilter === "all" || row.currency.startsWith(currencyFilter);
      const matchesType =
        typeFilter === "all" ||
        row.buyType === typeFilter ||
        row.sellType === typeFilter;
      return matchesSearch && matchesCurrency && matchesType;
    });
  }, [transformedData, search, currencyFilter, typeFilter]);

  return (
    <div className="grid gap-8 p-6 py-12">
      <div>
        <p className="text-2xl font-medium text-primary-3">Markets</p>
      </div>

      <div className="rounded-[12px] border border-[#EAE6EA] bg-card shadow-sm p-6 flex flex-col gap-6">
        <h3 className="text-base font-semibold text-primary-3">
          Market rate proposal
        </h3>

        <div className="flex flex-wrap items-end gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-70">
              <SearchIcon />
            </span>
            <Input
              placeholder="Search asset pair"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 border-grey-300 rounded-[8px] bg-off-white-1"
            />
          </div>

          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex flex-col gap-1.5 min-w-[120px]">
              <label className="text-xs text-gray-80">Currency</label>
              <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
                <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-3 w-[120px]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {pairsLoading && <SelectItem value="loading" disabled>Loading...</SelectItem>}
                  {pairsError && <SelectItem value="error" disabled>Error loading pairs</SelectItem>}
                  {Array.isArray(pairs) && pairs.map((pair) => (
                    pair.base && pair.quote && (
                      <SelectItem key={pair.id} value={`${pair.base}/${pair.quote}`}>
                        {`${pair.base}/${pair.quote}`}
                      </SelectItem>
                    )
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5 min-w-[120px]">
              <label className="text-xs text-gray-80">Type</label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-3 w-[120px]">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="FLAT">FLAT</SelectItem>
                  <SelectItem value="MANUAL">MANUAL</SelectItem>
                  <SelectItem value="PERCENTAGE">PERCENTAGE</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              className="ml-auto h-10 bg-primary hover:bg-primary/90 text-white rounded-[8px] px-4"
              onClick={() => setAddRateOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add new
            </Button>
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          <CustomTable
            columns={columns}
            data={filteredData}
            isLoading={isLoading}
          />
          {error && <p className="text-red-500 p-4">{error}</p>}
        </div>
      </div>

      <AddNewRate
        open={addRateOpen}
        onOpenChange={setAddRateOpen}
        pairs={pairs}
        pairsLoading={pairsLoading}
        onSuccess={fetchMarketRates}
      />

      <EditRate
        open={editRateOpen}
        onOpenChange={setEditRateOpen}
        rate={editingRate}
        onSuccess={fetchMarketRates}
      />

      <DeleteConfirm
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        rateId={deletingRate?.id || ""}
        currencyPair={deletingRate?.currency || ""}
        onSuccess={fetchMarketRates}
      />
    </div>
  );
};

export default Markets;
